import { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api.service';

interface ProviderRateItem {
  id: string;
  provider: string;
  model: string;
  rate_type: string;
  input_per_1m_tokens: string | null;
  output_per_1m_tokens: string | null;
  tokenizer: string | null;
  base_rate_per_hour: string | null;
  diarization_per_hour: string | null;
  overhead_factor: string | null;
  effective_from: string | null;
  verified_at: string | null;
  days_since_verified: number | null;
  staleness: string;
  notes: string | null;
}

interface DriftReport {
  provider: string;
  period_days: number;
  our_computed_cost: string;
  actual_billed_cost: string | null;
  drift_percent: string | null;
  level: string;
  error: string | null;
}

// Rate editing was removed — rates now live in app/core/cost_constants.py
// and update via PR. The DB rows are populated at deploy time. Drift
// detection is automated and surfaces here.

const STALENESS_BADGE: Record<string, { bg: string; text: string; icon: React.ElementType; label: string }> = {
  ok: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon, label: 'OK' },
  stale_warning: { bg: 'bg-amber-100', text: 'text-amber-800', icon: ExclamationTriangleIcon, label: 'Stale' },
  stale_critical: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon, label: 'Stale' },
  unverified: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon, label: 'Unverified' },
};

export default function RateManagement() {
  const [rates, setRates] = useState<ProviderRateItem[]>([]);
  const [drift, setDrift] = useState<DriftReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const [migrationNeeded, setMigrationNeeded] = useState(false);

  async function loadData() {
    setLoading(true);
    setMigrationNeeded(false);
    try {
      const [ratesRes, driftRes] = await Promise.allSettled([
        apiService.client.get('/admin/rates'),
        apiService.client.get('/admin/rates/drift'),
      ]);
      if (ratesRes.status === 'fulfilled') {
        setRates(ratesRes.value.data.rates);
        if (ratesRes.value.data.error) setMigrationNeeded(true);
      }
      if (driftRes.status === 'fulfilled') setDrift(driftRes.value.data);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-32 bg-white rounded-xl shadow" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md bg-indigo-50 px-4 py-3 ring-1 ring-inset ring-indigo-200">
        <div className="flex items-start gap-2">
          <CodeBracketIcon className="mt-0.5 h-5 w-5 text-indigo-700" aria-hidden="true" />
          <div className="text-sm text-indigo-900">
            <p className="font-medium">Rates live in code, not in this UI.</p>
            <p className="mt-1 text-indigo-800">
              The single source of truth is{' '}
              <code className="rounded bg-white px-1 py-0.5 text-xs font-mono ring-1 ring-indigo-200">
                app/core/cost_constants.py
              </code>
              . Updates ship as PRs. This page reads the deployed values and surfaces
              automated drift detection — you cannot (and shouldn&apos;t) edit them
              from here.
            </p>
          </div>
        </div>
      </div>

      {migrationNeeded && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              The provider rates table hasn&apos;t been created yet. Run{' '}
              <code className="rounded bg-amber-100 px-1 py-0.5 text-xs font-mono">alembic upgrade head</code>
              {' '}to set up rate management.
            </p>
          </div>
        </div>
      )}

      {/* Provider Rates Table */}
      <div className="rounded-xl bg-white shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Provider Rates</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Input/1M</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Output/1M</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective from</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rates.map((rate) => {
                const badge = STALENESS_BADGE[rate.staleness] || STALENESS_BADGE.unverified;
                const Icon = badge.icon;
                return (
                  <tr key={rate.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{rate.provider}</td>
                    <td className="px-4 py-3 text-gray-700">{rate.model}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {rate.rate_type === 'llm' && rate.input_per_1m_tokens
                        ? `$${rate.input_per_1m_tokens}`
                        : rate.base_rate_per_hour
                          ? `$${rate.base_rate_per_hour}/hr`
                          : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {rate.rate_type === 'llm' && rate.output_per_1m_tokens
                        ? `$${rate.output_per_1m_tokens}`
                        : rate.diarization_per_hour
                          ? `+$${rate.diarization_per_hour}/hr`
                          : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {rate.effective_from
                        ? new Date(rate.effective_from).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.bg} ${badge.text}`}>
                        <Icon className="h-3 w-3" />
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drift Detection */}
      {drift && (
        <div className="rounded-xl bg-white shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Drift Detection (Last {drift.period_days} Days)
            </h3>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <dt className="text-xs text-gray-500">Our Computed</dt>
              <dd className="text-lg font-semibold text-gray-900">
                ${drift.our_computed_cost}
              </dd>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <dt className="text-xs text-gray-500">Actual Billed</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {drift.actual_billed_cost ? `$${drift.actual_billed_cost}` : '—'}
              </dd>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <dt className="text-xs text-gray-500">Drift</dt>
              <dd className={`text-lg font-semibold ${
                drift.level === 'ok' ? 'text-green-600' :
                drift.level === 'warning' ? 'text-amber-600' : 'text-red-600'
              }`}>
                {drift.drift_percent ? `${drift.drift_percent}%` : '—'}
              </dd>
            </div>
          </div>

          {drift.error && (
            <p className="mt-3 text-sm text-gray-500">{drift.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

